import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { FileBarChart, Loader2, Settings2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { formatDate, formatDateTime } from '@/lib/utils/date';
import { templateLabel } from '../types';
import { useCvcSummary, useGenerateCampaignReport, type CvcSummaryRow } from '../api';

const num = (v: number | string | undefined): string => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString() : String(v);
};

const text = (v: string | number | undefined): string => {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
};

export function CvcSummaryPage() {
  const userId = useAuthStore((s) => s.userId);
  const isSubOrSuper = useAuthStore((s) => s.isSubOrSuperAdmin());
  const cvc = useCvcSummary();
  const generate = useGenerateCampaignReport();

  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [hasSearched, setHasSearched] = useState(false);

  const rows = cvc.data ?? [];

  const handleSearch = () => {
    if (startDate && endDate && startDate > endDate) {
      toast.error('Start date cannot be after end date.');
      return;
    }
    setHasSearched(true);
    cvc.mutate(
      {
        userId: Number(userId),
        startDate: startDate ? formatDate(startDate) : '',
        endDate: endDate ? formatDate(endDate) : '',
      },
      { onError: () => toast.error('Could not fetch CVC summary.') },
    );
  };

  const handleGenerate = (row: CvcSummaryRow) => {
    if (!row.campaignId) return;
    generate.mutate(
      {
        campaignId: row.campaignId,
        reportType: 'full',
        userId: Number(userId),
      },
      {
        onSuccess: (res) => toast.success(res?.message ?? 'Report queued'),
        onError: () => toast.error('Could not generate the report.'),
      },
    );
  };

  const columns = useMemo<ColumnDef<CvcSummaryRow>[]>(
    () => [
      {
        accessorKey: 'campaignId',
        header: 'Campaign ID',
        size: 120,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.campaignId}
          </span>
        ),
      },
      { accessorKey: 'campaignName', header: 'Campaign' },
      ...(isSubOrSuper
        ? [
            { accessorKey: 'userId', header: 'User ID', size: 100 } as ColumnDef<CvcSummaryRow>,
            { accessorKey: 'username', header: 'User', size: 160 } as ColumnDef<CvcSummaryRow>,
          ]
        : []),
      {
        accessorKey: 'campaignType',
        header: 'Type',
        size: 140,
        cell: ({ row }) => templateLabel(row.original.campaignType),
      },
      {
        accessorKey: 'scheduleDate',
        header: 'Scheduled',
        size: 180,
        cell: ({ row }) => formatDateTime(row.original.scheduleDate),
      },
      {
        accessorKey: 'startTime',
        header: 'Start',
        size: 110,
        cell: ({ row }) => text(row.original.startTime),
      },
      {
        accessorKey: 'endDate',
        header: 'End date',
        size: 180,
        cell: ({ row }) => formatDateTime(row.original.endDate),
      },
      {
        accessorKey: 'endtime',
        header: 'End time',
        size: 110,
        cell: ({ row }) => text(row.original.endtime),
      },
      {
        accessorKey: 'reportDate',
        header: 'Report date',
        size: 140,
        cell: ({ row }) => text(row.original.reportDate),
      },
      {
        accessorKey: 'totalNumbers',
        header: 'Total',
        size: 100,
        cell: ({ row }) => num(row.original.totalNumbers),
      },
      {
        accessorKey: 'totalDnd',
        header: 'DND',
        size: 100,
        cell: ({ row }) => num(row.original.totalDnd),
      },
      {
        accessorKey: 'totalDialed',
        header: 'Dialed',
        size: 110,
        cell: ({ row }) => num(row.original.totalDialed),
      },
      {
        accessorKey: 'totalConnectedCalls',
        header: 'Connected',
        size: 120,
        cell: ({ row }) => num(row.original.totalConnectedCalls),
      },
      {
        accessorKey: 'totalFailedCalls',
        header: 'Failed',
        size: 110,
        cell: ({ row }) => num(row.original.totalFailedCalls),
      },
      {
        accessorKey: 'totalduration',
        header: 'Duration',
        size: 110,
        cell: ({ row }) => num(row.original.totalduration),
      },
      {
        accessorKey: 'totalPulseduration',
        header: 'Pulse duration',
        size: 140,
        cell: ({ row }) => num(row.original.totalPulseduration),
      },
      {
        accessorKey: 'totalPulses',
        header: 'Pulses',
        size: 110,
        cell: ({ row }) => num(row.original.totalPulses),
      },
      {
        id: 'actions',
        header: 'Settings',
        size: 110,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label="Row actions"
              >
                <Settings2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="truncate">
                {row.original.campaignName}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleGenerate(row.original)}>
                <FileBarChart className="h-4 w-4" />
                Generate report
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSubOrSuper],
  );

  return (
    <FillPage>
      <PageHeader
        title="Continuous Campaigns (CvC)"
        description="Continuous voice-campaign roll-ups by date range."
      />

      <Card>
        <CardHeader>
          <CardTitle>Filter</CardTitle>
          <CardDescription>
            Leave dates empty to fetch the latest snapshot.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <div className="space-y-1.5">
              <Label htmlFor="start-date">Start date</Label>
              <DatePicker
                value={startDate}
                onChange={setStartDate}
                placeholder="Pick a start date"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="end-date">End date</Label>
              <DatePicker
                value={endDate}
                onChange={setEndDate}
                placeholder="Pick an end date"
              />
            </div>
            <Button onClick={handleSearch} disabled={cvc.isPending}>
              {cvc.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      <DataTable<CvcSummaryRow, unknown>
        tableId="campaigns:cvc:v1"
        columns={columns}
        data={rows}
        isLoading={cvc.isPending}
        searchPlaceholder="Filter results…"
        emptyTitle={hasSearched ? 'No data for that range' : 'Pick a range to begin'}
        emptyDescription={
          hasSearched
            ? 'Try widening the date range.'
            : 'Choose start / end dates above and click Search.'
        }
        getRowId={(row, i) => String(row.id ?? row.campaignId ?? i)}
        initialColumnPinning={{ right: ['actions'] }}
        exportFilename="cvc-summary.csv"
        pageSize={10}
      />
    </FillPage>
  );
}
